<template>
  <ResponsiveLayout>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p
          class="kds-typography-display-small"
          v-html="$t('b2b__partner_search__intro__header')"
        />
        <p
          class="kds-typography-paragraph-large"
          v-html="$t('b2b__partner_search__intro__description')"
        />
      </div>

      <PartnerSearchCtrl
        :pending-results="pendingResults"
        :loading="loadingSearchResults"
        @search="handleSearch"
        @ctrl-state-change="handlePartnerSearchCtrlState"
      />

      <BaseBanner
        v-if="showNotFoundWarningBox"
        variant="important"
        :title="$t('b2b__partner_search__not_found_warning__header')"
      >
        <p v-html="$t('b2b__partner_search__not_found_warning__description')" />
      </BaseBanner>
    </div>
    <template #footer><FooterLinks v-if="!isFromApp()" /></template>
  </ResponsiveLayout>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { debounce } from '@shared/utils'

import { searchCorporate } from '@shared/api-endpoints'

import ROUTE from '@us/app/router/appModule/names'
import { isTerminatedForSwordMigration } from '@shared/data/models/corporate.js'

import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import PartnerSearchCtrl from '@us/app/components/PartnerSearchCtrl.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import { CanceledError } from 'axios'
import FooterLinks from '@us/app/components/FooterLinks.vue'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import HeaderHelp from '@us/app/components/HeaderHelp.vue'

const router = useRouter()

const loadingSearchResults = ref(false)
const searchResults = ref([])
const pendingResults = ref(null)
const showNotFoundWarningBox = ref(false)
const ctrlItemDisabled = ref(false)

let searchRequestAbortController

const debouncedSearchCorporate = debounce(async (search, token, callback) => {
  try {
    const result = await searchCorporate(search, '', token)
    callback(result.data.corporates)
  } catch (error) {
    if (error instanceof CanceledError) {
      // expected
    } else {
      throw error
    }
  }
}, 250)

const handleSearch = (searchTerm) => {
  const cleanedSearchTerm = searchTerm.trim()
  if (!cleanedSearchTerm || cleanedSearchTerm.length <= 2) return

  loadingSearchResults.value = false
  searchResults.value = []

  if (searchRequestAbortController) {
    searchRequestAbortController.abort(
      'Cancel corporate search as newer search request is fired',
    )
  }

  searchRequestAbortController = new AbortController()

  loadingSearchResults.value = true

  debouncedSearchCorporate(
    cleanedSearchTerm,
    searchRequestAbortController.signal,
    (corporates) => {
      pendingResults.value = corporates
      loadingSearchResults.value = false
    },
  )
}

const handlePartnerSelectedEvent = (partner) => {
  router.push({
    name: ROUTE.SELECT_VERIFICATION_METHOD,
    params: {
      slug: partner.key,
    },
  })
}

const handlePartnerSearchCtrlState = ({ state, value }) => {
  ctrlItemDisabled.value = false

  switch (state) {
    case 'active':
      showNotFoundWarningBox.value = false
      break

    case 'disabled':
      if (isTerminatedForSwordMigration(value)) {
        router.push({ name: ROUTE.SWORD_REDIRECT })
      } else {
        showNotFoundWarningBox.value = true
      }
      break

    case 'processing':
      ctrlItemDisabled.value = true
      break

    case 'validated':
      ctrlItemDisabled.value = true
      handlePartnerSelectedEvent(value)
      break

    default:
      break
  }
}
</script>
